import type { ProblemDetails } from "@modelve/api/models/errorModels";
import { AuthenticationSessionError } from "auth/session";
import axios, { type AxiosResponse } from "axios";

export interface ApiErrorDetails {
  code?: string;
  status?: number;
  title: string;
  detail: string;
  traceId?: string;
}

export class ApiError extends Error implements ApiErrorDetails {
  readonly code?: string;
  readonly status?: number;
  readonly title: string;
  readonly detail: string;
  readonly traceId?: string;

  constructor(cause: unknown) {
    const details = toApiError(cause);
    super(details.detail, { cause });
    this.name = "ApiError";
    this.code = details.code;
    this.status = details.status;
    this.title = details.title;
    this.detail = details.detail;
    this.traceId = details.traceId;
  }
}

export function wrapApiError(error: unknown): unknown {
  if (error instanceof ApiError) return error;
  if (axios.isCancel(error)) return error;
  if (!axios.isAxiosError(error)) return error;
  return new ApiError(error);
}

interface FallbackTrans {
  title: { key: string; defaultValue: string };
  detail: { key: string; defaultValue: string };
}

export const statusFallbacks: Record<number, FallbackTrans> = {
  401: {
    title: {
      key: "error.message.401_title",
      defaultValue: "Your session has expired",
    },
    detail: {
      key: "error.message.401_detail",
      defaultValue: "Please sign in again.",
    },
  },
  402: {
    title: {
      key: "error.message.402_title",
      defaultValue: "Subscription access is required",
    },
    detail: {
      key: "error.message.402_detail",
      defaultValue:
        "Your subscription or usage allowance does not permit this request.",
    },
  },
  403: {
    title: {
      key: "error.message.403_title",
      defaultValue: "Access could not be established",
    },
    detail: {
      key: "error.message.403_detail",
      defaultValue: "You do not have access to the requested resource.",
    },
  },
  404: {
    title: {
      key: "error.message.404_title",
      defaultValue: "Resource not found",
    },
    detail: {
      key: "error.message.404_detail",
      defaultValue:
        "The requested resource was not found or is no longer available.",
    },
  },
  408: {
    title: {
      key: "error.message.408_title",
      defaultValue: "The request timed out",
    },
    detail: {
      key: "error.message.408_detail",
      defaultValue: "The service took too long to respond.",
    },
  },
  429: {
    title: {
      key: "error.message.429_title",
      defaultValue: "Too many requests",
    },
    detail: {
      key: "error.message.429_detail",
      defaultValue: "Please wait a moment and try again.",
    },
  },
  500: {
    title: {
      key: "error.message.500_title",
      defaultValue: "Something went wrong",
    },
    detail: {
      key: "error.message.500_detail",
      defaultValue: "The service encountered an unexpected error.",
    },
  },
  502: {
    title: {
      key: "error.message.502_title",
      defaultValue: "The service is temporarily unavailable",
    },
    detail: {
      key: "error.message.502_detail",
      defaultValue: "An upstream service did not respond successfully.",
    },
  },
  503: {
    title: {
      key: "error.message.503_title",
      defaultValue: "The service is temporarily unavailable",
    },
    detail: {
      key: "error.message.503_detail",
      defaultValue:
        "Please try again or check the Modelve status page for updates.",
    },
  },
  504: {
    title: {
      key: "error.message.504_title",
      defaultValue: "The request timed out",
    },
    detail: {
      key: "error.message.504_detail",
      defaultValue: "An upstream service took too long to respond.",
    },
  },
};

const genericFallback: FallbackTrans = {
  title: {
    key: "error.message.generic_title",
    defaultValue: "Something went wrong",
  },
  detail: {
    key: "error.message.generic_detail",
    defaultValue: "Please try again.",
  },
};

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value != null && !Array.isArray(value);
}

const problemDetailsStringFields = [
  "type",
  "title",
  "detail",
  "instance",
  "traceId",
] as const;

function isProblemDetails(value: unknown): value is ProblemDetails {
  if (!isRecord(value)) return false;

  const hasKnownField =
    "status" in value ||
    problemDetailsStringFields.some((field) => field in value);
  const hasValidStringFields = problemDetailsStringFields.every(
    (field) => !(field in value) || typeof value[field] === "string",
  );
  const hasValidStatus =
    !("status" in value) ||
    (typeof value.status === "number" &&
      Number.isInteger(value.status) &&
      value.status >= 100 &&
      value.status <= 599);

  return hasKnownField && hasValidStringFields && hasValidStatus;
}

function getProblemDetails(data: unknown): ProblemDetails | undefined {
  return isProblemDetails(data) ? data : undefined;
}

function nonEmptyString(value: unknown) {
  return typeof value === "string" && value.trim() ? value.trim() : undefined;
}

interface ParsedAxiosApiError {
  status?: number;
  problem?: ProblemDetails;
  defaultTitle: string;
  defaultDetail: string;
  traceId?: string;
}

function parseAxiosApiError(error: unknown): ParsedAxiosApiError {
  if (!axios.isAxiosError(error))
    return {
      defaultTitle: genericFallback.title.defaultValue,
      defaultDetail:
        error instanceof Error
          ? (nonEmptyString(error.message) ??
            genericFallback.detail.defaultValue)
          : genericFallback.detail.defaultValue,
    };

  const status = error.response?.status ?? error.status;
  const problem = getProblemDetails(error.response?.data);
  const fallback = status != null ? statusFallbacks[status] : undefined;

  return {
    status,
    problem,
    defaultTitle:
      fallback?.title.defaultValue ?? genericFallback.title.defaultValue,
    defaultDetail:
      fallback?.detail.defaultValue ?? genericFallback.detail.defaultValue,
    traceId: nonEmptyString(problem?.traceId),
  };
}

export function toApiError(error: unknown): ApiErrorDetails {
  if (error instanceof ApiError) return error;

  const parsed = parseAxiosApiError(error);
  const apiError: ApiErrorDetails = {
    status: parsed.status,
    title: nonEmptyString(parsed.problem?.title) ?? parsed.defaultTitle,
    detail: nonEmptyString(parsed.problem?.detail) ?? parsed.defaultDetail,
    traceId: parsed.traceId,
  };
  const code = nonEmptyString(parsed.problem?.code);
  if (code) apiError.code = code;
  return apiError;
}

export function isCancelledRequest(error: unknown): boolean {
  if (error instanceof ApiError) return isCancelledRequest(error.cause);

  return axios.isCancel(error);
}

export function isAuthenticationError(error: unknown): boolean {
  if (error instanceof ApiError) return isAuthenticationError(error.cause);

  return (
    (axios.isAxiosError(error) &&
      (error.response?.status ?? error.status) === 401) ||
    error instanceof AuthenticationSessionError
  );
}

export function isTransientApiError(error: unknown): boolean {
  if (error instanceof ApiError) return isTransientApiError(error.cause);

  if (!axios.isAxiosError(error) || isCancelledRequest(error)) return false;

  const status = error.response?.status ?? error.status;
  if (status == null) return true;
  return [408, 429, 500, 502, 503, 504].includes(status);
}

function getHeader(response: AxiosResponse | undefined, name: string) {
  const headers = response?.headers;
  if (!headers) return undefined;

  if (typeof headers.get === "function") {
    return headers.get(name);
  }

  return headers[name] ?? headers[name.toLowerCase()];
}

export function getRetryAfterMilliseconds(
  error: unknown,
  now: number = Date.now(),
): number | undefined {
  if (error instanceof ApiError) {
    return getRetryAfterMilliseconds(error.cause, now);
  }

  if (!axios.isAxiosError(error) || error.response?.status !== 429) {
    return undefined;
  }

  const value = getHeader(error.response, "retry-after");
  if (value == null) return undefined;

  const seconds = Number(value);
  if (Number.isFinite(seconds) && seconds >= 0) {
    return seconds * 1_000;
  }

  const date = Date.parse(String(value));
  if (Number.isNaN(date)) return undefined;
  return Math.max(0, date - now);
}
